import React, { useEffect, useState } from 'react'
import className from 'classnames/bind'
import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import customParseFormat from 'dayjs/plugin/customParseFormat'

import style from './MyhouseRealPrice.module.scss'

import SvgMyhouseMainPriceMore from '@Ui/images/myhouse/myhouse_main_price--more.svg'
import MyhouseRealPriceItem, { Props as MyhouseRealPriceItemProps } from '@Components/atoms/MyhouseRealPriceItem'
import RealPriceOnMonth from '@Types/entities/RealPriceOnMonth'
import RealPrice from '@Types/entities/RealPrice'
import { formatMoney, MONEY_FORMAT_TYPE } from '@Root/utils/moneyFormatter'
import useMain from '@Hooks/myhouse/main/useMain'
import { TRADE } from '@Root/contants'
import RealPriceTable from '@Types/entities/RealPriceTable'
import { handleNclickMix } from '@Helpers/nclick'
import { NCLICK } from '@Root/contants/lcs'

dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)
dayjs.extend(customParseFormat)

const cx = className.bind(style)

type Props = {
  real: RealPriceTable
  tradeType: TRADE
}

const LINK_TRADE: Record<TRADE, { makeLink: (complexNo: number, pyeongNo: number) => string }> = {
  [TRADE.DEAL]: {
    makeLink: (complexNo: number, pyeongNo: number) => `${process.env.REACT_APP_SPACE_WEB_URL}/complex/info/${complexNo}?ptpNo=${pyeongNo}#dealRealPrice`,
  },
  [TRADE.LEASE]: {
    makeLink: (complexNo: number, pyeongNo: number) => `${process.env.REACT_APP_SPACE_WEB_URL}/complex/info/${complexNo}?ptpNo=${pyeongNo}#leaseRealPrice`,
  },
  [TRADE.RENT]: {
    makeLink: (complexNo: number, pyeongNo: number) => `${process.env.REACT_APP_SPACE_WEB_URL}/complex/info/${complexNo}?ptpNo=${pyeongNo}#monthlyRentRealPrice`,
  },
  [TRADE.SHORTERMRENT]: {
    makeLink: (complexNo: number, pyeongNo: number) => `${process.env.REACT_APP_SPACE_WEB_URL}/complex/info/${complexNo}?ptpNo=${pyeongNo}#monthlyRentRealPrice`,
  },
}

const MyhouseRealPrice: React.FC<Props> = ({ real, tradeType }: Props) => {
  const { reduxMain } = useMain()
  const { house } = reduxMain

  const [date, setDate] = useState('')
  const [link, setLink] = useState('##')
  const [priceList, setPriceList] = useState([] as MyhouseRealPriceItemProps[])
  const [average, setAverage] = useState('')
  const [averageRange, setAverageRange] = useState('')

  const getFormatValue = (value: number) => {
    return formatMoney(value, MONEY_FORMAT_TYPE.MOBILE)
  }

  useEffect(() => {
    const realPriceList = real?.realPriceOnMonthList?.map((realPriceOnMonth: RealPriceOnMonth) => realPriceOnMonth.realPriceList).flat() || []
    const parsedPriceList =
      realPriceList.map((realPrice: RealPrice) => {
        return {
          date: realPrice.formattedTradeYearMonth,
          price: realPrice.formattedPrice,
          floor: realPrice.floor + '층',
        }
      }) || []

    setPriceList(parsedPriceList)

    setDate(dayjs(real?.realPriceBasisYearMonth).format('YYYY.MM'))
    setAverageRange('')
    setAverage('')

    for (const range of [3, 6]) {
      const startDate = dayjs().add(-range, 'month').startOf('day')
      const endDate = dayjs().endOf('day')

      const recentPriceList = realPriceList.filter((realPrice) => {
        const tradeDate = dayjs(realPrice.formattedTradeYearMonth, 'YYYY.MM.DD')
        return tradeDate.isSameOrAfter(startDate) && tradeDate.isSameOrBefore(endDate)
      })

      if (recentPriceList.length !== 0) {
        if (tradeType === TRADE.DEAL) {
          const sum = recentPriceList.map((realPrice: RealPrice) => realPrice.dealPrice).reduce((sum, dealPrice) => sum + dealPrice)
          const average = sum / recentPriceList.length
          setAverageRange(String(range))
          setAverage(getFormatValue(average))
        } else if (tradeType === TRADE.LEASE) {
          const sum = recentPriceList.map((realPrice: RealPrice) => realPrice.leasePrice).reduce((sum, leasePrice) => sum + leasePrice)
          const average = sum / recentPriceList.length
          setAverageRange(String(range))
          setAverage(getFormatValue(average))
        }
        break
      }
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    setLink(LINK_TRADE[tradeType]?.makeLink(house?.complexNo, house?.pyeongNo) || '#')
  }, [real])

  return (
    <div className={cx('myhouse_real_price')}>
      <div className={cx('myhouse_price_header')}>
        <strong className={cx('myhouse_header_title')}>최근 실거래가</strong>
        {date !== 'Invalid Date' && <em className={cx('myhouse_header_standard')}>{`${date}. 국토교통부 기준`}</em>}
      </div>
      {(priceList && priceList.length > 0 && (
        <>
          {!!average && (
            <div className={cx('myhouse_price_average')}>
              <span className={cx('myhouse_average_title')}>{averageRange}개월 평균가</span>
              <strong className={cx('myhouse_average_value')}>{average}</strong>
            </div>
          )}
          <ol className={cx('myhouse_price_list')}>
            {priceList
              ?.filter((priceItem, i) => i < 3)
              .map((priceItem, index) => {
                return <MyhouseRealPriceItem key={`realPrice_${index}`} {...priceItem} />
              })}
          </ol>
          <a href={link} className={cx('myhouse_link_more')} onClick={handleNclickMix(NCLICK.MY_HOUSE.REAL_PRICE.MORE)}>
            <span className={cx('myhouse_link_title')}>실거래가 더보기</span>
            <SvgMyhouseMainPriceMore />
          </a>
        </>
      )) || <p className={cx('myhouse_nodata_text')}>실거래가 내역이 없습니다.</p>}
    </div>
  )
}

export default MyhouseRealPrice
